import React, {memo, useCallback} from "react";
import {Box, CircularProgress, IconButton, Stack, Tooltip, Typography} from "@mui/material";
import {AddCircleOutline, ArrowDownwardRounded, CloudDownloadOutlined, ContentCopy, PlaylistAddRounded, PlaylistPlayRounded, PlaylistRemoveRounded} from "@mui/icons-material";
import Utils from "../../../../../../../core/services/utils/utils";
import {getPreventativeAutocompleteComponent} from "../../../../../../views/panel/operator/settings/import/components/preventative-correction-dialog/cells/preventative-autocomplete-registry";
import PreventativeCommittedTextField from "./preventative-committed-text-field";
import {splitPredefinedImportTokens} from "../../../../../../views/panel/operator/settings/import/engine/transformers/shared/resolve-predefined-property-values";
import {getPartAutocompletePropsFromImportContext} from "../../../../../../views/panel/operator/settings/import/engine/shared/import-industry-context";
import {getPreventativeAutocompleteActionDialogs, isPreventativeAutocompleteType, PreventativeAutocompleteType, fieldSupportsAddPropertyOption,} from "../../../../../../views/panel/operator/settings/import/engine/preventative-interactions/shared";
import {usePreventativeGridActions} from "../../../../../../views/panel/operator/settings/import/components/preventative-correction-dialog/preventative-grid-actions-context";
import ToggleButtons from "../../../../../base/button/toggle_buttons";
import {dataGridCellTextProps} from "../../../../../../../packages/data-grid";
import CopyToClipboard from "../../../../../base/copy-to-clipboard";

function formatCaptionOriginal(originalRaw) {
    if (originalRaw === null || originalRaw === undefined) {
        return '';
    }
    if (Array.isArray(originalRaw)) {
        return originalRaw.map((entry) => String(entry).trim()).filter(Boolean).join(', ');
    }
    if (typeof originalRaw === 'object') {
        return '';
    }
    return String(originalRaw);
}

function getReadonlyDisplayText(field, originalRaw, committedRaw) {
    if (originalRaw !== committedRaw) {
        return typeof field.extractValue === 'function'
            ? field.extractValue(committedRaw)
            : committedRaw;
    }
    return originalRaw;
}

const CellActions = memo(({
                              t,
                              fieldType,
                              bulkSourceValue,
                              setForAll,
                              setForRemaining,
                              onOpenCreation,
                              onOpenImport,
                              onAddPropertyOption,
                              showAddPropertyOption = false,
                              addingPropertyOption = false,
                              originalRaw,
                          }) => {
    const {creationDialog, importDialog} = getPreventativeAutocompleteActionDialogs(fieldType);
    const showBulk = bulkSourceValue !== originalRaw && bulkSourceValue !== undefined;

    return (
        <Stack direction="row" spacing={0.5} sx={{ml: 1}}>
            {showBulk && setForAll && (
                <Tooltip title={t('applyToAllTooltip')}>
                    <IconButton size="small" onClick={() => setForAll(bulkSourceValue)} color="primary">
                        <PlaylistPlayRounded/>
                    </IconButton>
                </Tooltip>
            )}
            {showBulk && setForRemaining && (
                <Tooltip title={t('applyToRemainingTooltip')}>
                    <IconButton size="small" onClick={() => setForRemaining(bulkSourceValue)} color="primary">
                        <PlaylistRemoveRounded/>
                    </IconButton>
                </Tooltip>
            )}
            {creationDialog === 'product' && (
                <Tooltip title={t('createProductTooltip')}>
                    <IconButton size="small" onClick={onOpenCreation} color="primary">
                        <AddCircleOutline fontSize="small"/>
                    </IconButton>
                </Tooltip>
            )}
            {creationDialog === 'customer' && (
                <Tooltip title={t('createCustomerTooltip')}>
                    <IconButton size="small" onClick={onOpenCreation} color="primary">
                        <AddCircleOutline fontSize="small"/>
                    </IconButton>
                </Tooltip>
            )}
            {creationDialog === 'vendor' && (
                <Tooltip title={t('createVendorTooltip')}>
                    <IconButton size="small" onClick={onOpenCreation} color="primary">
                        <AddCircleOutline fontSize="small"/>
                    </IconButton>
                </Tooltip>
            )}
            {creationDialog === 'tag' && (
                <Tooltip title={t('createTagTooltip')}>
                    <IconButton size="small" onClick={onOpenCreation} color="primary">
                        <AddCircleOutline fontSize="small"/>
                    </IconButton>
                </Tooltip>
            )}
            {importDialog === 'product' && (
                <Tooltip title={t('importProductTooltip')}>
                    <IconButton size="small" onClick={onOpenImport} color="secondary">
                        <CloudDownloadOutlined/>
                    </IconButton>
                </Tooltip>
            )}
            {importDialog === 'customer' && (
                <Tooltip title={t('importCustomerTooltip')}>
                    <IconButton size="small" onClick={onOpenImport} color="secondary">
                        <CloudDownloadOutlined/>
                    </IconButton>
                </Tooltip>
            )}
            {showAddPropertyOption && (
                <Tooltip title={t('addPropertyOptionTooltip')}>
                    <span>
                        <IconButton
                            size="small"
                            onClick={onAddPropertyOption}
                            color="primary"
                            disabled={addingPropertyOption}
                        >
                            {addingPropertyOption
                                ? <CircularProgress size={16} color="inherit"/>
                                : <PlaylistAddRounded fontSize="small"/>}
                        </IconButton>
                    </span>
                </Tooltip>
            )}
        </Stack>
    );
});

const matchOptionTitle = (token, options) => {
    const trimmed = (token ?? '').trim();
    if (!trimmed) return null;
    const exact = options?.find((o) => o.title === trimmed);
    if (exact) return exact;
    const upper = trimmed.toUpperCase();
    return options?.find((o) => (o.title ?? '').trim().toUpperCase() === upper) ?? null;
};

/**
 * Entity autocompletes expect option objects. CSV scalars must not be passed as `value`
 * or MUI warns and selection fails.
 */
const ENTITY_AUTOCOMPLETE_TYPES = new Set([
    PreventativeAutocompleteType.PART,
    PreventativeAutocompleteType.EXTERNAL_PART,
    PreventativeAutocompleteType.WAREHOUSE,
    PreventativeAutocompleteType.VENDOR,
    PreventativeAutocompleteType.CUSTOMER,
    PreventativeAutocompleteType.EXTERNAL_CUSTOMER,
    PreventativeAutocompleteType.PARTNER,
    PreventativeAutocompleteType.TAG,
    PreventativeAutocompleteType.FORM,
    PreventativeAutocompleteType.PRODUCT_RELATION,
    PreventativeAutocompleteType.TERRITORY,
    PreventativeAutocompleteType.SALES_PERSON,
]);

const parsePredefinedAutocompleteValue = (raw, options, multiple, autocompleteType) => {
    switch (autocompleteType) {
        case PreventativeAutocompleteType.INDUSTRY_PROPERTY_OPTIONS: {
            if (!raw) return multiple ? [] : null;
            if (multiple) {
                const tokens = typeof raw === 'string' ? splitPredefinedImportTokens(raw) : [];
                return tokens.map((t) => matchOptionTitle(t, options)).filter(Boolean);
            }
            if (typeof raw === 'object' && raw?.title != null) {
                return raw;
            }
            return matchOptionTitle(String(raw), options);
        }
        default: {
            if (!raw) return multiple ? [] : null;
            if (ENTITY_AUTOCOMPLETE_TYPES.has(autocompleteType) && typeof raw !== 'object') {
                return multiple ? [] : null;
            }
            return raw;
        }
    }
};

const PreventativeFieldCell = memo(({
                                        rowIndex,
                                        field,
                                        issue,
                                        patchFieldKey,
                                        staticDataContext,
                                        columnTitle,
                                        originalRaw,
                                        committedValue,
                                        bulkSourceValue,
                                        onPatchRow,
                                        setForAll,
                                        setForRemaining,
                                        skipCheckbox,
                                        skipped = false,
                                        skippedLabel = '',
                                        t,
                                    }) => {
    const {openCreationForCell, openImportForCell, addPropertyOptionForCell, addingPropertyOptionCellKey} = usePreventativeGridActions();
    const fieldKey = patchFieldKey ?? field.key;
    const propertyOptionCellKey = `${rowIndex}:${field.key}`;
    const addingPropertyOption = addingPropertyOptionCellKey === propertyOptionCellKey;
    const originalCaption = formatCaptionOriginal(originalRaw);
    const isEdited = committedValue !== originalRaw;

    const handleCommitScalar = useCallback((next) => {
        onPatchRow(rowIndex, fieldKey, next);
    }, [onPatchRow, rowIndex, fieldKey]);

    const handleAutocompleteChange = useCallback((_e, val) => {
        onPatchRow(rowIndex, fieldKey, val);
    }, [onPatchRow, rowIndex, fieldKey]);

    const handleToggleChange = useCallback((checked) => {
        onPatchRow(rowIndex, fieldKey, checked);
    }, [onPatchRow, rowIndex, fieldKey]);

    const wrapWithCaption = useCallback((children, {showAddPropertyOption = false} = {}) => (
        <Stack direction="column" spacing={0.5} sx={{width: '100%'}}>
            <Typography
                {...dataGridCellTextProps}
                variant="caption"
                component="div"
                color="text.secondary"
                sx={{textDecoration: isEdited ? 'line-through' : 'none'}}
            >
                {originalCaption}
                {
                    !!originalCaption &&
                    <CopyToClipboard>
                        {({copy}) =>
                            <IconButton
                                size={'small'}
                                color={'secondary'}
                                className={'ml-1'}
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    copy(originalCaption);
                                }}
                            >
                                <ContentCopy className={'icon small'}/>
                            </IconButton>
                        }
                    </CopyToClipboard>
                }
                {
                    originalCaption && isEdited &&
                    <Tooltip title={t('revertToOriginalTooltip')}>
                        <IconButton
                            size={'small'}
                            color={'secondary'}
                            className={'ml-1'}
                            onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleCommitScalar(originalRaw);
                            }}
                        >
                            <ArrowDownwardRounded className={'icon small'}/>
                        </IconButton>
                    </Tooltip>
                }
            </Typography>
            <Box sx={{display: 'flex', alignItems: 'center', width: '100%', gap: 0.5}}>
                {skipCheckbox}
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        opacity: skipped ? 0.55 : 1,
                    }}
                >
                    {skipped ? (
                        <Typography variant="body2" color="text.disabled" fontStyle="italic" noWrap>
                            {skippedLabel}
                        </Typography>
                    ) : (
                        <>
                            <Box sx={{flex: 1, minWidth: 0}}>{children}</Box>
                            <CellActions
                                t={t}
                                fieldType={field.type}
                                bulkSourceValue={bulkSourceValue}
                                setForAll={setForAll}
                                setForRemaining={setForRemaining}
                                originalRaw={originalRaw}
                                onOpenCreation={() => openCreationForCell(rowIndex, field.key)}
                                onOpenImport={() => openImportForCell(rowIndex, field.key)}
                                showAddPropertyOption={showAddPropertyOption}
                                addingPropertyOption={addingPropertyOption}
                                onAddPropertyOption={() => addPropertyOptionForCell(rowIndex, field.key)}
                            />
                        </>
                    )}
                </Box>
            </Box>
        </Stack>
    ), [isEdited, originalCaption, t, field.type, field.key, bulkSourceValue, setForAll, setForRemaining, handleCommitScalar, originalRaw, openCreationForCell, rowIndex, openImportForCell, addPropertyOptionForCell, addingPropertyOption, skipCheckbox, skipped, skippedLabel]);

    if (field.appliesToIssue && issue && !field.appliesToIssue(issue)) {
        return (
            <Typography variant="body2" color="text.disabled">
                —
            </Typography>
        );
    }

    if (field.type === 'text') {
        const text = getReadonlyDisplayText(field, originalRaw, committedValue);
        const display = text === null || text === undefined ? '' : String(text);
        const textContent = (
            <Typography
                variant="body2"
                color="text.primary"
                sx={field.multiline ? {whiteSpace: 'pre-wrap'} : undefined}
            >
                {display}
            </Typography>
        );

        if (!skipCheckbox) {
            return textContent;
        }

        return (
            <Box sx={{display: 'flex', alignItems: 'center', width: '100%', gap: 0.5}}>
                {skipCheckbox}
                <Box sx={{flex: 1, minWidth: 0, opacity: skipped ? 0.55 : 1}}>
                    {skipped ? (
                        <Typography variant="body2" color="text.disabled" fontStyle="italic" noWrap>
                            {skippedLabel}
                        </Typography>
                    ) : (
                        textContent
                    )}
                </Box>
            </Box>
        );
    }

    if (field.type === 'toggle') {
        let value = Utils.tryConvertStringToBoolean(committedValue);
        if (typeof value === 'boolean') {
            value = value.toString();
        }
        return wrapWithCaption(
            <ToggleButtons
                size={'small'}
                value={value}
                onChange={(_, v) => handleToggleChange(v)}
                fullWidth
                options={[
                    {name: t('toggleYes'), value: 'true',},
                    {name: t('toggleNo'), value: 'false',},
                ]}
            />
        )
    }

    if (isPreventativeAutocompleteType(field.type)) {
        const Component = getPreventativeAutocompleteComponent(field.type);
        if (!Component) {
            return (
                <Typography variant="caption" color="error">
                    {t('unknownAutocompleteType', {type: field.type})}
                </Typography>
            );
        }
        const contextProps =
            typeof field.autocompletePropsFromContext === 'function'
                ? field.autocompletePropsFromContext(staticDataContext)
                : {};
        const issueOrFieldProps =
            typeof field.autocompletePropsFromIssue === 'function'
                ? field.autocompletePropsFromIssue(issue, staticDataContext)
                : (field.autocompleteProps || {});
        const autocompleteProps = {
            ...(field.type === PreventativeAutocompleteType.PART
                ? getPartAutocompletePropsFromImportContext(staticDataContext)
                : {}),
            ...contextProps,
            ...issueOrFieldProps,
        };
        const multiple = !!autocompleteProps.multiple;
        const options = autocompleteProps.cachedOptions ?? [];
        const autocompleteValue = parsePredefinedAutocompleteValue(committedValue, options, multiple, field.type);
        const invalidPredefinedMeta = issue?.meta?.predefinedOptions?.[field.key];
        const rawCandidate =
            invalidPredefinedMeta?.unresolvedTokens?.[0] ??
            (typeof field.extractValue === 'function'
                ? field.extractValue(originalRaw, issue)
                : (originalRaw ?? '').toString().trim());
        const committedResolved = multiple
            ? Array.isArray(autocompleteValue) && autocompleteValue.length > 0
            : !!(autocompleteValue && typeof autocompleteValue === 'object' && autocompleteValue.id != null);
        const showAddPropertyOption =
            fieldSupportsAddPropertyOption(field.type) &&
            !!invalidPredefinedMeta &&
            !!rawCandidate &&
            !committedResolved;

        return wrapWithCaption(
            <Component
                {...autocompleteProps}
                textFieldProps={{
                    size: 'small',
                    fullWidth: true,
                    ...(autocompleteProps.textFieldProps || {}),
                }}
                label={columnTitle ?? field.label ?? t('selectValueLabel')}
                value={autocompleteValue ?? (multiple ? [] : null)}
                onChange={handleAutocompleteChange}
                size="small"
                fullWidth
                variant="outlined"
            />,
            {showAddPropertyOption},
        );
    }

    return wrapWithCaption(
        <PreventativeCommittedTextField
            committedValue={committedValue}
            textFieldProps={field.textFieldProps}
            onCommit={handleCommitScalar}
        />,
    );
});

export default PreventativeFieldCell;
